import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Mail, ArrowLeft, Send, StickyNote, RefreshCw, RotateCw, Settings2, AlertCircle, Clock, Users, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["OPEN", "IN_PROGRESS", "WAITING", "RESOLVED"];
const STATUS_LABELS = { OPEN: "OPEN", IN_PROGRESS: "IN BEHANDELING", WAITING: "WACHT", RESOLVED: "OPGELOST" };
const toneMap = { OPEN: "bg-[#8A6D1F] text-white", IN_PROGRESS: "bg-[#1D5B2A] text-white", WAITING: "border border-[#E8E1D8]", RESOLVED: "bg-[#77736D] text-white" };

function EmailStatusBar({ onSynced }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const load = useCallback(() => api.get("/admin/support/status").then(({ data }) => setS(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  const be = s.business_email, te = s.transactional_email;
  const act = async (path, label) => {
    setBusy(label);
    try {
      const { data } = await api.post(path);
      if (path.includes("sync")) toast.success(data.configured ? `Sync: ${data.imported} geïmporteerd, ${data.failed} mislukt` : "Inbound niet geconfigureerd");
      else if (path.includes("test-inbound")) toast[data.ok ? "success" : "error"](data.ok ? "IMAP-verbinding OK" : `IMAP: ${data.error}`);
      else if (path.includes("test-outbound")) toast[data.ok ? "success" : "error"](data.ok ? "Test-e-mail verzonden" : "Uitgaand niet verzonden (zie Resend-domein)");
      else if (path.includes("retry")) toast.success(`Opnieuw verwerkt: ${data.reprocessed}, nog mislukt: ${data.still_failed}`);
      load(); onSynced?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const sendTestOutbound = async () => {
    setBusy("to");
    try {
      const { data } = await api.post("/admin/support/test-outbound", { to: testTo });
      toast[data.ok ? "success" : "error"](data.ok
        ? `Test verzonden naar ${data.recipient} (Resend id: ${data.email_id})`
        : `Niet verzonden: ${data.error || "zie Resend-domein"}`);
      load(); onSynced?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  return (
    <div className="border border-[#E8E1D8] bg-white mb-4" data-testid="email-status-bar">
      <div className="flex flex-wrap items-center gap-3 p-3">
        <span className={`label-x px-2 py-1 ${be.connected ? "bg-[#1D5B2A] text-white" : "bg-[#8A6D1F] text-white"}`} data-testid="inbound-state">INKOMEND: {be.state}</span>
        <span className={`label-x px-2 py-1 ${te.connected ? "bg-[#1D5B2A] text-white" : "border border-[#E8E1D8]"}`}>RESEND: {te.connected ? "AAN" : "UIT"}</span>
        {be.failed_messages > 0 && <span className="label-x text-[#9B1C1C] flex items-center gap-1"><AlertCircle size={12} /> {be.failed_messages} mislukt</span>}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => act("/admin/support/sync", "sync")} disabled={busy} className="label-x border border-[#E8E1D8] px-3 py-1.5 flex items-center gap-1" data-testid="inbound-sync-btn"><RefreshCw size={12} className={busy === "sync" ? "animate-spin" : ""} /> SYNCHRONISEER</button>
          <button onClick={() => setOpen((o) => !o)} className="label-x border border-[#E8E1D8] px-3 py-1.5 flex items-center gap-1" data-testid="email-settings-toggle"><Settings2 size={12} /> INSTELLINGEN</button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E8E1D8] p-4 text-xs space-y-2" data-testid="email-settings-panel">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[#3a3a38]">
            <p>Van: <b>{te.from_name} &lt;{te.from_address || "—"}&gt;</b></p>
            <p>Reply-to: <b>{te.reply_to || "—"}</b></p>
            <p>Inbound mailbox: <b>{be.inbound_address || "niet ingesteld"}</b></p>
            <p>IMAP host: <b>{be.imap_host || "niet ingesteld"}</b></p>
            <p>Laatste sync-poging: <b>{be.last_attempt ? new Date(be.last_attempt).toLocaleString("nl-NL") : "—"}</b></p>
            <p>Laatst succesvol: <b>{be.last_success ? new Date(be.last_success).toLocaleString("nl-NL") : "—"}</b></p>
            <p>Laatst geïmporteerd: <b>{be.last_imported ?? 0}</b></p>
            <p>Mislukte berichten: <b>{be.failed_messages ?? 0}</b></p>
          </div>
          {be.last_error && <p className="text-[#9B1C1C]">Laatste fout: {be.last_error}</p>}
          <div className="flex flex-wrap gap-2 pt-2 items-center">
            <button onClick={() => act("/admin/support/test-inbound", "ti")} disabled={busy} className="label-x border border-[#E8E1D8] px-3 py-1.5" data-testid="test-inbound-btn">TEST INBOUND</button>
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test-ontvanger (optioneel)" className="border border-[#E8E1D8] px-2 py-1.5 text-xs w-52 outline-none focus:border-[#1D1D1B]" data-testid="test-outbound-to" />
            <button onClick={sendTestOutbound} disabled={busy} className="label-x border border-[#E8E1D8] px-3 py-1.5" data-testid="test-outbound-btn">{busy === "to" ? "VERZENDEN…" : "TEST OUTBOUND"}</button>
            <button onClick={() => act("/admin/support/retry-failed", "rf")} disabled={busy} className="label-x border border-[#E8E1D8] px-3 py-1.5" data-testid="retry-failed-btn">RETRY MISLUKT</button>
          </div>
          <p className="text-[#77736D] pt-1">Testadres is alleen de ontvanger van de testmail; het wordt nooit als Reply-To opgeslagen. Uitgaande antwoorden gaan altijd via ONUSA &lt;{te.reply_to || "info@onusa.nl"}&gt;.</p>
        </div>
      )}
    </div>
  );
}

function linkify(text) {
  const re = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let last = 0, m, key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<a key={key++} href={m[0]} target="_blank" rel="noopener noreferrer" className="underline break-all">{m[0]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function Detail({ id, onBack, onChange }) {
  const [c, setC] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const msgsRef = useRef(null);
  const load = useCallback(() => api.get(`/admin/conversations/${id}`).then(({ data }) => setC(data)).catch(() => {}), [id]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const root = msgsRef.current;
    if (!root) return;
    root.querySelectorAll("a").forEach((a) => { a.setAttribute("target", "_blank"); a.setAttribute("rel", "noopener noreferrer"); });
  }, [c]);
  if (!c) return <p className="label-x p-6">LADEN…</p>;

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/conversations/${id}/reply`, { body: reply });
      toast.success(data.email_sent ? "Antwoord verzonden per e-mail" : "Opgeslagen (e-mail log-only tot Resend-domein geverifieerd)");
      setReply(""); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setStatus = async (status) => { await api.patch(`/admin/conversations/${id}`, { status }); load(); onChange?.(); };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/admin/conversations/${id}/note`, { body: note }); setNote(""); load(); };
  const retrySend = async (mid) => {
    try { const { data } = await api.post(`/admin/conversations/${id}/messages/${mid}/retry`); toast[data.email_sent ? "success" : "error"](data.email_sent ? "Opnieuw verzonden" : "Verzenden opnieuw mislukt"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm("Deze conversatie definitief verwijderen?")) return;
    try { await api.delete(`/admin/conversations/${id}`); toast.success("Conversatie verwijderd"); onChange?.(); onBack?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="conversation-detail">
      <button onClick={onBack} className="label-x flex items-center gap-1 mb-4 md:hidden" data-testid="conv-back"><ArrowLeft size={14} /> INBOX</button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><p className="heading-x text-lg">{c.subject}</p><p className="label-x mt-1">{c.customer_name} · {c.email}</p></div>
        <div className="flex items-center gap-2">
          <select value={c.status} onChange={(e) => setStatus(e.target.value)} className="border border-[#E8E1D8] bg-white px-2 py-1 text-xs font-mono" data-testid="conv-status">
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <button onClick={remove} className="label-x text-[#9B1C1C] border border-[#E8B4B4] px-2 py-1.5 flex items-center gap-1" data-testid="conv-delete"><Trash2 size={13} /> VERWIJDER</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_260px] gap-5">
        <div>
          <div ref={msgsRef} className="space-y-3 max-h-[46vh] overflow-y-auto no-scrollbar border border-[#E8E1D8] bg-white p-4">
            {c.messages.map((m) => (
              <div key={m.id} className={`p-3 text-sm ${m.direction === "outbound" ? "bg-[#1D1D1B] text-[#F7F5F1] ml-8" : "bg-[#E8E1D8]/50 mr-8"}`} data-testid={`msg-${m.direction}`}>
                {m.html
                  ? <div className="text-sm break-words leading-relaxed [&_a]:underline [&_a]:break-all [&_img]:max-w-full [&_img]:h-auto" dangerouslySetInnerHTML={{ __html: m.html }} />
                  : <p className="whitespace-pre-wrap break-words">{linkify(m.body || "")}</p>}
                <p className={`label-x mt-2 flex items-center gap-2 flex-wrap ${m.direction === "outbound" ? "text-[#F7F5F1]/60" : ""}`}>
                  <span>{m.direction === "outbound" ? `ONUSA · ${m.author}` : c.customer_name} · {new Date(m.created_at).toLocaleString("nl-NL")}</span>
                  {m.direction === "outbound" && m.email_sent === false && (
                    <button onClick={() => retrySend(m.id)} className="text-[#F7B1B1] flex items-center gap-1 underline" data-testid={`retry-send-${m.id}`}><RotateCw size={11} /> NIET VERZONDEN — OPNIEUW</button>
                  )}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Typ je antwoord in het Nederlands…" className="w-full border border-[#E8E1D8] bg-white px-3 py-2 text-sm outline-none focus:border-[#1D1D1B]" data-testid="reply-input" />
            <button onClick={sendReply} disabled={busy} className="btn-x mt-2 text-[0.65rem]" data-testid="reply-send"><Send size={13} /> {busy ? "VERZENDEN…" : "VERSTUUR ANTWOORD"}</button>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="border border-[#E8E1D8] bg-white p-4">
            <p className="label-x mb-2">KLANT</p>
            <p className="text-sm">{c.customer_name}</p>
            <p className="text-xs text-[#77736D]">{c.email}</p>
            {c.phone && <p className="text-xs text-[#77736D]">{c.phone}</p>}
            <p className="label-x mt-3 mb-1">BESTELLINGEN</p>
            {c.customer_context.orders.length === 0 ? <p className="text-xs text-[#77736D]">Geen bestellingen</p> :
              c.customer_context.orders.map((o) => <p key={o.number} className="text-xs">{o.number} · €{o.total?.toFixed(2)} · {o.payment_status}</p>)}
          </div>
          <div className="border border-[#E8E1D8] bg-white p-4">
            <p className="label-x mb-2 flex items-center gap-1"><StickyNote size={12} /> INTERNE NOTITIES</p>
            {(c.notes || []).map((n) => <p key={n.id} className="text-xs text-[#77736D] mb-1">{n.body} <span className="opacity-60">· {n.author}</span></p>)}
            <div className="flex gap-2 mt-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notitie…" className="flex-1 border border-[#E8E1D8] px-2 py-1 text-xs" data-testid="conv-note-input" />
              <button onClick={addNote} className="label-x" data-testid="conv-note-add">TOEVOEGEN</button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default function AdminInbox() {
  const [convs, setConvs] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const syncing = useRef(false);
  const load = useCallback(() => {
    const p = filter ? `?status=${filter}` : "";
    return api.get(`/admin/conversations${p}`).then(({ data }) => setConvs(data)).catch(() => {});
  }, [filter]);
  const autoSync = useCallback(async () => {
    if (syncing.current) return;
    syncing.current = true;
    try { await api.post("/admin/support/sync"); } catch { /* stil */ } finally { syncing.current = false; }
    load();
  }, [load]);
  useEffect(() => {
    load();
    autoSync();
    const t = setInterval(autoSync, 30000);
    return () => clearInterval(t);
  }, [load, autoSync]);

  const [grouped, setGrouped] = useState(true);
  const groups = useMemo(() => {
    const m = new Map();
    for (const c of convs) {
      const key = c.email || "onbekend";
      if (!m.has(key)) m.set(key, { email: key, name: c.customer_name || key, items: [], lastAt: c.last_message_at || "", unread: 0 });
      const g = m.get(key);
      g.items.push(c);
      if (c.unread) g.unread += 1;
      if ((c.last_message_at || "") > g.lastAt) g.lastAt = c.last_message_at || "";
      if (!g.name && c.customer_name) g.name = c.customer_name;
    }
    return Array.from(m.values()).sort((a, b) => (b.lastAt || "").localeCompare(a.lastAt || ""));
  }, [convs]);

  const renderCard = (c) => (
    <button key={c.id} onClick={() => setOpen(c.id)} className={`w-full text-left border p-4 transition-colors ${c.unread ? "border-[#1D1D1B] bg-[#FBF4E4] hover:bg-[#F7ECD4]" : "border-[#E8E1D8] bg-white hover:bg-[#E8E1D8]/30"}`} data-testid={`conv-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-sm truncate flex items-center gap-2 ${c.unread ? "font-semibold" : "font-medium"}`}>
            {c.unread && <span className="w-2 h-2 rounded-full bg-[#8A6D1F] shrink-0" data-testid={`conv-unread-dot-${c.id}`} />}
            <Mail size={13} /> {c.subject}
          </p>
          {!grouped && <p className="label-x mt-1">{c.customer_name} · {c.email}{c.related_order ? ` · ${c.related_order}` : ""}</p>}
          {grouped && c.related_order && <p className="label-x mt-1">{c.related_order}</p>}
          <p className="text-xs text-[#77736D] mt-1 truncate">{c.preview}</p>
          <p className="label-x mt-1 flex items-center gap-1 text-[#77736D]" data-testid={`conv-meta-${c.id}`}>
            <Clock size={11} /> {c.last_message_at ? new Date(c.last_message_at).toLocaleString("nl-NL") : "—"}
            <span className="mx-1">·</span>
            <span className={c.unread ? "text-[#8A6D1F]" : ""}>{c.unread ? "ONGELEZEN" : "GEOPEND"}</span>
          </p>
        </div>
        <span className={`label-x px-2 py-0.5 shrink-0 ${toneMap[c.status]}`}>{STATUS_LABELS[c.status] || c.status}</span>
      </div>
    </button>
  );

  if (open) return <div data-testid="admin-inbox"><Detail id={open} onBack={() => { setOpen(null); load(); }} onChange={load} /></div>;

  return (
    <div data-testid="admin-inbox">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-x text-2xl">KLANTENSERVICE</h1>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-[#E8E1D8] bg-white px-2 py-1.5 text-xs font-mono" data-testid="inbox-filter">
          <option value="">ALLE</option>{STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>
      <EmailStatusBar onSynced={load} />
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setGrouped((g) => !g)} className="label-x border border-[#E8E1D8] px-3 py-1.5 flex items-center gap-1" data-testid="group-toggle">
          <Users size={12} /> {grouped ? "GEGROEPEERD PER KLANT" : "PLATTE LIJST"}
        </button>
      </div>
      {convs.length === 0 && <p className="label-x p-8 text-center">GEEN BERICHTEN</p>}
      {grouped ? (
        <div className="space-y-5" data-testid="inbox-grouped">
          {groups.map((g) => (
            <div key={g.email} data-testid={`cust-group-${g.email}`}>
              <div className="flex items-center gap-2 mb-2 px-1 flex-wrap">
                <Users size={13} />
                <span className="text-sm font-semibold">{g.name}</span>
                <span className="label-x text-[#77736D]">{g.email}</span>
                <span className="label-x text-[#77736D]">· {g.items.length} {g.items.length === 1 ? "gesprek" : "gesprekken"}</span>
                {g.unread > 0 && <span className="label-x px-2 py-0.5 bg-[#8A6D1F] text-white" data-testid={`cust-group-unread-${g.email}`}>{g.unread} ONGELEZEN</span>}
              </div>
              <div className="space-y-2 border-l-2 border-[#E8E1D8] pl-3">
                {g.items.map(renderCard)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2" data-testid="inbox-flat">{convs.map(renderCard)}</div>
      )}
    </div>
  );
}
